import {
	CARD_COLOURS,
	cardLabel,
	DOTS_COUNT,
	type DotsRaceState,
	type LastCardState,
	MINE_SIZE,
	type MinesweeperState,
	type PlayerIndex,
} from "@playpal/core";

interface BoardProps<S> {
	state: S;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

// --- Minesweeper Duel -----------------------------------------------------

const NUMBER_COLOUR = [
	"",
	"text-sky-600",
	"text-emerald-600",
	"text-rose-600",
	"text-indigo-700",
	"text-amber-700",
	"text-teal-700",
	"text-slate-700",
	"text-slate-900",
];

export function MinesweeperBoard({ state, seat, onMove }: BoardProps<MinesweeperState>) {
	const myTurn = state.turn === seat && !state.result;
	const open = new Set(state.revealed);
	const boom = new Map(state.exploded.map((entry) => [entry.cell, entry.seat]));

	return (
		<div className="board-frame board-lg">
			<p className="mb-2 text-center text-lg font-semibold">
				{state.scores[0]} – {state.scores[1]} · {state.exploded.length} of 10 mines found the hard
				way
			</p>
			<div
				className="grid gap-0.5 rounded-2xl bg-slate-400/60 p-1.5 shadow-xl sm:gap-1"
				style={{ gridTemplateColumns: `repeat(${MINE_SIZE}, minmax(0, 1fr))` }}
			>
				{Array.from({ length: MINE_SIZE * MINE_SIZE }, (_, cell) => {
					const shown = open.has(cell);
					const exploded = boom.has(cell);
					const count = state.neighbours[cell];
					return (
						<button
							key={cell}
							type="button"
							disabled={!myTurn || shown || exploded}
							aria-label={`Row ${Math.floor(cell / MINE_SIZE) + 1}, column ${(cell % MINE_SIZE) + 1}${
								shown ? `, ${count} mines nearby` : exploded ? ", a mine went off here" : ""
							}`}
							onClick={() => onMove({ cell })}
							className={`flex aspect-square items-center justify-center rounded text-sm font-black transition sm:text-lg ${
								exploded
									? "bg-rose-500"
									: shown
										? `bg-white ${NUMBER_COLOUR[Math.max(0, count)] ?? ""}`
										: "bg-slate-300 enabled:hover:bg-slate-200 dark:bg-slate-600"
							}`}
						>
							{exploded ? "💣" : shown && count > 0 ? count : ""}
						</button>
					);
				})}
			</div>
			<p className="mt-3 h-6 text-center font-semibold" aria-live="polite">
				{state.lastNote ?? (myTurn ? "Clear a square" : "They're choosing…")}
			</p>
		</div>
	);
}

// --- Last Card Duel -------------------------------------------------------

export function LastCardBoard({ state, seat, onMove }: BoardProps<LastCardState>) {
	const myTurn = state.turn === seat && !state.result;
	const them: PlayerIndex = seat === 0 ? 1 : 0;
	const canPlaySomething = state.hands[seat].some(
		(card) => card.colour === state.top.colour || card.value === state.top.value,
	);

	return (
		<div className="board-frame board-lg text-center">
			<p className="mb-3 text-lg font-semibold">
				They hold {state.counts[them]} cards · {state.pileLeft} in the pile
			</p>

			<div className="mb-4 flex flex-col items-center">
				<span className="mb-1 text-sm font-bold opacity-70">On the table</span>
				<span className="flex h-28 w-20 items-center justify-center rounded-2xl bg-white text-4xl shadow-xl">
					{CARD_COLOURS[state.top.colour]}
					<span className="ml-1 font-black text-indigo-950">{cardLabel(state.top)}</span>
				</span>
			</div>

			<div className="mb-4 flex flex-wrap justify-center gap-2">
				{state.hands[seat].map((card, index) => {
					const ok = card.colour === state.top.colour || card.value === state.top.value;
					return (
						<button
							key={`${card.colour}-${card.value}-${index}`}
							type="button"
							disabled={!myTurn || !ok}
							aria-label={`Play ${cardLabel(card)}`}
							onClick={() => onMove({ play: index })}
							className={`flex h-24 w-16 flex-col items-center justify-center rounded-xl bg-white shadow-md transition enabled:hover:-translate-y-1 ${
								ok ? "" : "opacity-40"
							}`}
						>
							<span className="text-2xl">{CARD_COLOURS[card.colour]}</span>
							<span className="text-xl font-black text-indigo-950">{cardLabel(card)}</span>
						</button>
					);
				})}
			</div>

			{myTurn && !canPlaySomething && (
				<button
					type="button"
					onClick={() => onMove({ draw: true })}
					className="rounded-2xl bg-indigo-600 px-6 py-3 text-lg font-bold text-white shadow-lg"
				>
					🫳 Pick up a card
				</button>
			)}

			<p className="mt-3 h-6 font-semibold" aria-live="polite">
				{state.lastNote ?? (myTurn ? "Your go" : "They're playing…")}
			</p>
		</div>
	);
}

// --- Connect-the-Dots Race ------------------------------------------------

export function DotsRaceBoard({ state, seat, onMove }: BoardProps<DotsRaceState>) {
	const size = 8;
	const scale = 100 / size;
	const point = (index: number) => {
		const dot = state.dots[index];
		return { x: (dot.x + 0.5) * scale, y: (dot.y + 0.5) * scale };
	};

	return (
		<div className="board-frame board-md">
			<p className="mb-2 text-center text-lg font-semibold">
				Next number: {state.next + 1} · {state.scores[0]} – {state.scores[1]}
			</p>

			{/* biome-ignore lint/a11y/useSemanticElements: an <svg> cannot be a <fieldset>. */}
			<svg
				viewBox="0 0 100 100"
				className="w-full rounded-3xl bg-white/80 shadow-xl dark:bg-white/10"
				role="group"
				aria-label="Connect the dots"
			>
				<title>Connect the dots</title>
				{state.owner.map((owner, line) => {
					if (owner === null) return null;
					const from = point(line);
					const to = point(line + 1);
					return (
						<line
							key={line}
							x1={from.x}
							y1={from.y}
							x2={to.x}
							y2={to.y}
							strokeWidth={1.6}
							strokeLinecap="round"
							className={owner === 0 ? "stroke-rose-500" : "stroke-sky-500"}
						/>
					);
				})}

				{state.dots.map((_, index) => {
					const { x, y } = point(index);
					const isNext = index === state.next;
					return (
						<g key={index}>
							<circle
								cx={x}
								cy={y}
								r={isNext ? 4.5 : 3}
								className={
									isNext
										? "animate-pop fill-amber-400"
										: index < state.next
											? "fill-indigo-400"
											: "fill-indigo-900/40 dark:fill-indigo-100/40"
								}
							/>
							<text
								x={x}
								y={y + 1.2}
								textAnchor="middle"
								fontSize={3.4}
								className="fill-white font-bold"
							>
								{index + 1}
							</text>
							{isNext && (
								// biome-ignore lint/a11y/useSemanticElements: SVG has no <button>; this circle is the tap target.
								<circle
									cx={x}
									cy={y}
									r={7}
									className="cursor-pointer fill-transparent"
									role="button"
									tabIndex={0}
									aria-label={`Tap dot ${index + 1}`}
									onClick={() => onMove({ dot: index })}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											onMove({ dot: index });
										}
									}}
								/>
							)}
						</g>
					);
				})}
			</svg>

			<p className="mt-3 text-center font-semibold">
				{state.result
					? "Picture finished!"
					: `Tap number ${state.next + 1} before your friend — ${DOTS_COUNT - 1} lines in all.`}
			</p>
			<p className="mt-1 text-center text-sm opacity-70">
				You draw in {seat === 0 ? "red" : "blue"}.
			</p>
		</div>
	);
}
